import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, AlertTriangle, CheckCircle, Clock, Globe, Smartphone, Key, Database, ArrowLeft } from 'lucide-react';
import { useDisciplineStore } from '../../contexts/disciplineStore';

const SecurityStatsComponent = ({ isDarkMode = true, onBackToOverview }) => {
  // Get Digital Discipline data for enhanced security metrics
  const { goals, timers, xp, streak } = useDisciplineStore();
  
  // Calculate security-related discipline metrics
  const disciplineSecurityMetrics = React.useMemo(() => {
    const securityGoals = goals.filter(g => 
      g.category === 'productivity' || 
      g.title?.toLowerCase().includes('security') ||
      g.title?.toLowerCase().includes('privacy') ||
      g.title?.toLowerCase().includes('backup')
    );
    
    const securityTimers = timers.filter(t => 
      t.goalId && goals.find(g => 
        g.id === t.goalId && 
        (g.category === 'productivity' || 
         g.title?.toLowerCase().includes('security'))
      )
    );
    
    const completedSecurityTimers = securityTimers.filter(t => t.isCompleted);
    
    return {
      securityAwarenessSessions: completedSecurityTimers.length,
      disciplineSecurityScore: Math.min(100, 60 + (streak * 2) + (completedSecurityTimers.length * 3)),
      securityFocusTime: completedSecurityTimers.reduce((total, timer) => {
        return total + Math.round((timer.duration || 25) / 60);
      }, 0)
    };
  }, [goals, timers, streak]);

  const [securityData, setSecurityData] = useState({
    loginActivity: {
      totalLogins: 342,
      successfulLogins: 338,
      failedAttempts: 4,
      lastLogin: '2025-05-28 09:15:00',
      averageSessionDuration: '2h 15m',
      uniqueDevices: 3
    },
    accountSecurity: {
      securityScore: Math.max(85, disciplineSecurityMetrics.disciplineSecurityScore),
      twoFactorEnabled: true,
      biometricEnabled: true,
      passwordStrength: 'Strong',
      lastPasswordChange: '2025-04-15',
      recoveryMethodsCount: 2
    },
    blockchain: {
      walletTransactions: 127,
      smartContractInteractions: 23,
      lastBlockchainActivity: '2025-05-27 16:30:00',
      encryptedDataEntries: 1453 + (disciplineSecurityMetrics.securityAwarenessSessions * 10),
      backupValidations: 12 + Math.floor(disciplineSecurityMetrics.securityFocusTime / 60)
    },
    privacy: {
      dataEncryptionLevel: 'AES-256',
      privacyScore: Math.min(100, 82 + disciplineSecurityMetrics.securityAwarenessSessions),
      permissionsGranted: 8,
      dataSharedApps: 2,
      anonymousDataPoints: 567
    },
    discipline: disciplineSecurityMetrics
  });

  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    // Simulate data loading based on time range
    const loadSecurityData = () => {
      // In a real app, this would fetch data from your security service
      console.log('Loading security data for:', timeRange);
    };
    loadSecurityData();
  }, [timeRange]);

  const SecurityCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        {trend && (
          <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-600">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );

  const SecurityScoreRing = ({ score, label }) => {
    const circumference = 2 * Math.PI * 45;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke={score >= 80 ? "#10b981" : score >= 60 ? "#f59e0b" : "#ef4444"}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-300"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-gray-900">{score}</span>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2 text-center">{label}</p>
      </div>
    );
  };

  const LoginActivityChart = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const loginCounts = [12, 8, 15, 10, 14, 6, 4];
    const maxLogins = Math.max(...loginCounts);

    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Login Activity</h3>
        <div className="flex items-end justify-between h-32 space-x-2">
          {days.map((day, index) => (
            <div key={day} className="flex flex-col items-center flex-1">
              <div
                className="w-full bg-blue-500 rounded-t transition-all duration-300 min-h-[4px]"
                style={{
                  height: `${(loginCounts[index] / maxLogins) * 100}%`,
                }}
              />
              <span className="text-xs text-gray-600 mt-2">{day}</span>
              <span className="text-xs text-gray-400">{loginCounts[index]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const SecurityEvents = () => {
    const events = [
      {
        id: 1,
        type: 'success',
        message: 'Successful login from new device',
        timestamp: '2 hours ago',
        icon: CheckCircle
      },
      {
        id: 2,
        type: 'warning',
        message: 'Password change recommended',
        timestamp: '1 day ago',
        icon: AlertTriangle
      },
      {
        id: 3,
        type: 'info',
        message: 'Backup verification completed',
        timestamp: '3 days ago',
        icon: Shield
      },
      {
        id: 4,
        type: 'success',
        message: 'Two-factor authentication enabled',
        timestamp: '1 week ago',
        icon: Lock
      }
    ];

    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Security Events</h3>
        <div className="space-y-3">
          {events.map((event) => {
            const Icon = event.icon;
            return (
              <div key={event.id} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                <Icon className={`w-5 h-5 ${
                  event.type === 'success' ? 'text-green-600' :
                  event.type === 'warning' ? 'text-yellow-600' :
                  'text-blue-600'
                }`} />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{event.message}</p>
                  <p className="text-xs text-gray-500">{event.timestamp}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const BlockchainMetrics = () => (
    <div className="bg-white rounded-lg p-6 shadow-sm border">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Blockchain Security</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{securityData.blockchain.walletTransactions}</div>
          <p className="text-sm text-gray-600">Wallet Transactions</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{securityData.blockchain.smartContractInteractions}</div>
          <p className="text-sm text-gray-600">Smart Contracts</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{securityData.blockchain.encryptedDataEntries.toLocaleString()}</div>
          <p className="text-sm text-gray-600">Encrypted Entries</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{securityData.blockchain.backupValidations}</div>
          <p className="text-sm text-gray-600">Backup Validations</p>
        </div>
      </div>
    </div>
  );
  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={onBackToOverview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Security & Privacy</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
        </select>
      </div>

      {/* Security Scores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SecurityScoreRing score={securityData.accountSecurity.securityScore} label="Security Score" />
        <SecurityScoreRing score={securityData.privacy.privacyScore} label="Privacy Score" />
        <SecurityScoreRing score={Math.round((securityData.loginActivity.successfulLogins / securityData.loginActivity.totalLogins) * 100)} label="Login Success Rate" />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SecurityCard
          title="Total Logins"
          value={securityData.loginActivity.totalLogins}
          subtitle="All time"
          icon={Eye}
          trend={5}
          color="blue"
        />
        <SecurityCard
          title="Failed Attempts"
          value={securityData.loginActivity.failedAttempts}
          subtitle="This month"
          icon={AlertTriangle}
          trend={-25}
          color="red"
        />
        <SecurityCard
          title="Active Devices"
          value={securityData.loginActivity.uniqueDevices}
          subtitle="Authorized"
          icon={Smartphone}
          color="green"
        />
        <SecurityCard
          title="Session Duration"
          value={securityData.loginActivity.averageSessionDuration}
          subtitle="Average"
          icon={Clock}
          color="purple"
        />
      </div>

      {/* Charts and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LoginActivityChart />
        <SecurityEvents />
      </div>

      {/* Security Features */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Features</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Lock className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-900">Two-Factor Authentication</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                securityData.accountSecurity.twoFactorEnabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {securityData.accountSecurity.twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Key className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-900">Biometric Authentication</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                securityData.accountSecurity.biometricEnabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {securityData.accountSecurity.biometricEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Database className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-900">Data Encryption</span>
              </div>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {securityData.privacy.dataEncryptionLevel}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-gray-900">Password Strength</span>
              </div>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {securityData.accountSecurity.passwordStrength}
              </span>
            </div>
          </div>
        </div>

        <BlockchainMetrics />
      </div>      {/* Digital Discipline Security Section */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Digital Discipline Security</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">{securityData.discipline.securityAwarenessSessions}</div>
            <p className="text-sm text-gray-600">Security Focus Sessions</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">{securityData.discipline.securityFocusTime}h</div>
            <p className="text-sm text-gray-600">Security Focus Time</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">{securityData.discipline.disciplineSecurityScore}</div>
            <p className="text-sm text-gray-600">Discipline Security Score</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">{streak}</div>
            <p className="text-sm text-gray-600">Discipline Streak</p>
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Shield className="w-5 h-5 text-green-600" />
            <h4 className="font-medium text-green-900">Security Awareness Status</h4>
          </div>
          <p className="text-sm text-green-700">
            Your security discipline is {securityData.discipline.disciplineSecurityScore >= 80 ? 'excellent' : 
            securityData.discipline.disciplineSecurityScore >= 60 ? 'good' : 'needs improvement'}. 
            Keep focusing on security-related goals to maintain high protection levels.
          </p>
          {securityData.discipline.securityAwarenessSessions > 0 && (
            <p className="text-xs text-green-600 mt-2">
              Recent security-focused sessions have boosted your protection by {securityData.discipline.securityAwarenessSessions * 3} points!
            </p>
          )}
        </div>
      </div>

      {/* Privacy Information */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy & Data Management</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-xl font-bold text-indigo-600">{securityData.privacy.permissionsGranted}</div>
            <p className="text-sm text-gray-600">App Permissions</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-indigo-600">{securityData.privacy.dataSharedApps}</div>
            <p className="text-sm text-gray-600">Data Sharing</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-indigo-600">{securityData.privacy.anonymousDataPoints}</div>
            <p className="text-sm text-gray-600">Anonymous Points</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-indigo-600">{securityData.accountSecurity.recoveryMethodsCount}</div>
            <p className="text-sm text-gray-600">Recovery Methods</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityStatsComponent;
